/** Server-owned automatic analysis ceilings, frozen with Run; these are upper bounds, not generated-answer estimates. */
import * as z from 'zod';
const optionalAutomaticResponsePhraseBudgetSchema = z.discriminatedUnion('enabled', [
    z
        .object({
        enabled: z.literal(false),
        reason: z.enum(['node_disabled', 'not_parent_comparison', 'historical_run']),
    })
        .strict(),
    z
        .object({
        enabled: z.literal(true),
        policyVersion: z.literal('automatic-response-codebook.v1'),
        model: z.null(),
        display: z.object({ heading: z.string(), lines: z.array(z.string()).min(1) }).strict(),
        selection: z.object({ mode: z.literal('all') }).strict(),
        maxRequests: z.literal(0),
        maxInputBytesPerRequest: z.literal(0),
        maxInputTokensTotalUpperBound: z.literal(0),
        maxOutputTokensPerRequest: z.literal(0),
        maxOutputTokensTotal: z.literal(0),
        maxRequestDurationMs: z.literal(0),
        maxTotalRequestDurationMs: z.literal(0),
        providerConcurrency: z.literal(1),
        automaticRetries: z.literal(0),
        boundMethod: z.literal('Dictionary scanning makes no provider or embedding calls.'),
    })
        .strict(),
]);
const requiredInquiryCallBudgetSchema = z
    .object({
    model: z.string().min(1),
    maxRequests: z.number().int().nonnegative(),
    maxOutputTokensPerRequest: z.number().int().positive(),
    timeoutMs: z.number().int().positive(),
    automaticRetries: z.literal(0),
    reasoningEffort: z.string().nullable(),
})
    .strict();
/** Admitted follow-ups are required work, not the historical zero-call dictionary scan. */
const requiredInquiryBudgetSchema = z
    .object({
    enabled: z.literal(true),
    policyVersion: z.literal('required-respondent-inquiry.v1'),
    display: z.object({ heading: z.string(), lines: z.array(z.string()).min(1) }).strict(),
    respondentsPerArm: z.number().int().nonnegative(),
    armCount: z.number().int().nonnegative(),
    conversations: z.number().int().nonnegative(),
    maxRequests: z.number().int().nonnegative(),
    questionWriter: requiredInquiryCallBudgetSchema,
    answering: requiredInquiryCallBudgetSchema,
    retrievalEmbeddings: z
        .strictObject({
        model: z.string().min(1),
        dimensions: z.number().int().positive(),
        maxRequests: z.number().int().nonnegative(),
        automaticRetries: z.literal(0),
    })
        .optional(),
    /** Summary calls beyond the analysis allowance, which already holds one fact-check per batch. Upper bounds. */
    summary: z
        .strictObject({
        embeddingModel: z.string().min(1),
        maxCombineRounds: z.number().int().nonnegative(),
        maxNotes: z.number().int().positive(),
        embedMaxRequests: z.number().int().nonnegative(),
        mergeMaxRequests: z.number().int().nonnegative(),
        mergeEmbeddingMaxRequests: z.number().int().nonnegative(),
        factCheckMaxRequests: z.number().int().nonnegative(),
        resolveMaxRequests: z.number().int().nonnegative(),
        maxRequests: z.number().int().nonnegative(),
        /** Merge, fact-check and resolve requests after a clear server error; included in their ceilings. */
        automaticRetries: z.number().int().nonnegative(),
        embeddingAutomaticRetries: z.literal(0),
    })
        .optional(),
    analysis: requiredInquiryCallBudgetSchema
        .extend({
        /** Extraction and fact-check requests after a clear server error; included in maxRequests. */
        automaticRetries: z.number().int().nonnegative(),
        maxInputBytesPerRequest: z.number().int().positive(),
        synthesis: requiredInquiryCallBudgetSchema
            .omit({ maxRequests: true })
            .extend({
            maxInputBytesPerRequest: z.number().int().positive(),
            stages: z
                .object({
                initial: z.object({ model: z.string().min(1), reasoningEffort: z.string() }).strict(),
                review: z.object({ model: z.string().min(1), reasoningEffort: z.string() }).strict(),
                final: z.object({ model: z.string().min(1), reasoningEffort: z.string() }).strict(),
            })
                .strict(),
        })
            .strict(),
    })
        .strict(),
})
    .strict()
    .superRefine((budget, context) => {
    if (budget.conversations !== budget.respondentsPerArm * budget.armCount ||
        budget.questionWriter.maxRequests !== budget.conversations ||
        budget.answering.maxRequests !== budget.conversations ||
        budget.maxRequests !==
            budget.questionWriter.maxRequests +
                budget.answering.maxRequests +
                budget.analysis.maxRequests +
                (budget.retrievalEmbeddings?.maxRequests ?? 0) +
                (budget.summary?.maxRequests ?? 0) ||
        (budget.summary &&
            (budget.summary.maxRequests !==
                budget.summary.embedMaxRequests +
                    budget.summary.mergeMaxRequests +
                    budget.summary.mergeEmbeddingMaxRequests +
                    budget.summary.resolveMaxRequests ||
                budget.summary.factCheckMaxRequests * 2 > budget.analysis.maxRequests)))
        context.addIssue({
            code: 'custom',
            message: 'Required work must reconcile to the complete accepted conversation inventory',
        });
});
export const automaticResponsePhraseBudgetSchema = z.union([
    optionalAutomaticResponsePhraseBudgetSchema,
    requiredInquiryBudgetSchema,
]);
//# sourceMappingURL=study-automatic-enrichment.js.map